// Helpers for the "master account for kitchen receipt on IP printing" socket flow.
// State lives in localStorage so every tab of this browser shares it.

export const KITCHEN_RECEIPT_MASTER_ACCOUNT_ORDER_EVENT = "kitchenReceiptIPPrintingMasterAccountOrder";
export const KITCHEN_RECEIPT_MASTER_ACCOUNT_OPEN_ORDERS_REFRESH = "kitchenReceiptMasterAccountOpenOrdersRefresh";
export const KITCHEN_RECEIPT_PRINT_STATUS_EVENT = "kitchenReceiptPrintStatus";

export const KITCHEN_RECEIPT_PRINT_STATUS = {
  PENDING: "pending",
  PRINTING: "printing",
  PRINTED: "printed",
  FAILED: "failed",
  SKIPPED: "skipped",
};

export const isFinalKitchenReceiptPrintStatus = (status) =>
  [KITCHEN_RECEIPT_PRINT_STATUS.PRINTED, KITCHEN_RECEIPT_PRINT_STATUS.FAILED, KITCHEN_RECEIPT_PRINT_STATUS.SKIPPED].includes(status);

const PROCESSED_EVENTS_KEY = "kitchenReceiptMasterAccountProcessedEvents";
const ORDER_SNAPSHOTS_KEY = "kitchenReceiptMasterAccountOrderSnapshots";
const LOCK_NAME = "kitchenReceiptMasterAccountPrint";
const RETENTION_MS = 24 * 60 * 60 * 1000;
const MAX_ENTRIES = 300;

const readStore = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || {};
  } catch (error) {
    return {};
  }
};

const writeStore = (key, value) => {
  const now = Date.now();
  const entries = Object.entries(value)
    .filter(([, entry]) => now - (entry?.savedAt || 0) < RETENTION_MS)
    .sort(([, a], [, b]) => (b?.savedAt || 0) - (a?.savedAt || 0))
    .slice(0, MAX_ENTRIES);

  try {
    localStorage.setItem(key, JSON.stringify(Object.fromEntries(entries)));
  } catch (error) {
    console.error(error);
  }
};

// Runs one event at a time across all tabs of this browser, so check-and-mark stays atomic.
export const runKitchenReceiptMasterAccountExclusive = (task) => {
  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    return navigator.locks.request(LOCK_NAME, task);
  }
  return task();
};

// One key per saved version of an order, so a repeated delivery of the same save is ignored
// while a later save of the same order is still handled.
export const getOrderVersionKey = (order) =>
  `${order?.id || order?._id}:${order?.updatedAt || ""}`;

export const isOrderVersionProcessed = (versionKey) =>
  Boolean(readStore(PROCESSED_EVENTS_KEY)[versionKey]);

export const markOrderVersionProcessed = (versionKey) => {
  const processed = readStore(PROCESSED_EVENTS_KEY);
  processed[versionKey] = { savedAt: Date.now() };
  writeStore(PROCESSED_EVENTS_KEY, processed);
};

const getItemKey = (item, index) =>
  String(item?.cartItemId || item?._id || item?.id || `index-${index}`);

const getItemSignature = (item) =>
  JSON.stringify({
    qty: item?.qty ?? item?.quantity ?? null,
    isVoidItem: item?.isVoidItem === true,
    variant: item?.selectedVariant?.id || item?.selectedVariant?._id || item?.selectedVariant?.name || null,
    notes: item?.notes || "",
    groups: (item?.groups || []).map((group) => [
      group?.id || group?._id || group?.name || "",
      (group?.items || []).map((option) => [
        option?.id || option?._id || option?.name || "",
        option?.qty ?? option?.quantity ?? null,
      ]),
    ]),
  });

export const saveOrderSnapshot = (order) => {
  const orderId = String(order?.id || order?._id || "");
  if (!orderId) return;

  const items = {};
  (order?.items || []).forEach((item, index) => {
    items[getItemKey(item, index)] = getItemSignature(item);
  });

  const snapshots = readStore(ORDER_SNAPSHOTS_KEY);
  snapshots[orderId] = { items, savedAt: Date.now() };
  writeStore(ORDER_SNAPSHOTS_KEY, snapshots);
};

// Returns the order items shaped for hasStationDataChanged: an item is "not printed" when it is new
// or changed since this browser last saw the order. Without a snapshot every item counts as changed,
// so the kitchen gets a full slip rather than missing one.
export const getItemsForStationChangeCheck = (order) => {
  const orderId = String(order?.id || order?._id || "");
  const snapshot = readStore(ORDER_SNAPSHOTS_KEY)[orderId];

  return (order?.items || []).map((item, index) => {
    const previousSignature = snapshot?.items?.[getItemKey(item, index)];
    const isChanged = !snapshot || previousSignature !== getItemSignature(item);

    return {
      categoryId: item?.categoryId,
      isPrinted: !isChanged,
      isVoidItem: isChanged && item?.isVoidItem === true,
    };
  });
};

const getEntityId = (value) => String(value?.id || value?._id || value || "");

// True when the venue routes kitchen slips for POS orders to a single master account.
export const isKitchenSlipHandledByMasterAccount = (user) => {
  const { configurations, kitchenReceiptIPPrintingMasterAccountId } = user?.venue ?? {};
  return (
    configurations?.isKitchenReceiptPrinterIPConfigEnabled === true &&
    configurations?.isKitchenReceiptIPPrintingMasterAccountEnabled === true &&
    Boolean(getEntityId(kitchenReceiptIPPrintingMasterAccountId))
  );
};

// True for every account except the master account while the master account setting is on.
// Those accounts skip their own kitchen slip after saving an order; the master account prints it.
export const shouldSkipLocalKitchenSlip = (user) =>
  isKitchenSlipHandledByMasterAccount(user) &&
  getEntityId(user?.venue?.kitchenReceiptIPPrintingMasterAccountId) !== String(user?.id);
